/**
 * Sprint-6 C.1.2 — Per-Property Invoice-Template-Zuweisung.
 *
 * Sprint-16-Refresh: Card-Wrapper wie PropertyForm-Sections, Shared-Select.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { FileText } from 'lucide-react';
import { FormField, Pill, Select } from '@/shared/components';
import { toApiError, useApi, type PropertyResponse } from '@/shared/api';
import { PropertyFormSectionHeader } from '../property-form/ChannelsPushedSection';
import { propertyKeys } from '../queries';

interface Props {
  property: PropertyResponse;
}

export function InvoiceTemplateAssignCard({ property }: Props) {
  const api = useApi();
  const qc = useQueryClient();

  const { data: templates, isLoading } = useQuery({
    queryKey: ['invoiceTemplates'],
    queryFn: () => api.invoiceTemplates.list(),
  });

  const assign = useMutation({
    mutationFn: (templateId: string | null) =>
      api.invoiceTemplates.assignToProperty(property.id, { templateId }),
    onSuccess: () => {
      toast.success('Rechnungsvorlage zugewiesen');
      void qc.invalidateQueries({ queryKey: propertyKeys.detail(property.id) });
    },
    onError: async (err) => {
      const e = await toApiError(err);
      toast.error(e.detail ?? e.title);
    },
  });

  const primary = templates?.find((t) => t.isPrimary) ?? null;
  const selected = property.invoiceTemplateId ?? '';

  return (
    <div className="bg-card border-line/50 shadow-card rounded-lg border p-6">
      {/* Sprint 55 C3: „Tenant-Primary" und „Primary" waren Datenbanksprache auf einer Seite,
          die ein Betreiber bedient. Fachlich unverändert — es ist dieselbe Zuweisung. */}
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <PropertyFormSectionHeader
          icon={<FileText size={13} strokeWidth={2} />}
          iconTint="navy"
          title="Rechnungsvorlage"
          subtitle="Bestimmt Layout und Kopfdaten jeder Rechnung dieses Apartments. Ohne eigene Wahl gilt die Standardvorlage des Workspace."
        />
        {!property.invoiceTemplateId && primary && (
          <Pill variant="live" size="sm">
            Standardvorlage: {primary.name}
          </Pill>
        )}
      </div>

      {isLoading ? (
        <p className="text-ink-soft text-sm">Vorlagen werden geladen…</p>
      ) : (
        <FormField label="Vorlage auswählen">
          <Select
            value={selected}
            onChange={(e) => {
              const v = e.target.value;
              assign.mutate(v === '' ? null : v);
            }}
            disabled={assign.isPending}
          >
            <option value="">
              Standardvorlage des Workspace
              {primary ? ` (${primary.name})` : ' — noch keine hinterlegt'}
            </option>
            {templates
              ?.filter((t) => !t.isPrimary)
              .map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            {primary && (
              <option key={primary.id} value={primary.id}>
                {primary.name} (Standardvorlage, fest gewählt)
              </option>
            )}
          </Select>
        </FormField>
      )}
    </div>
  );
}
